import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Storefront, CheckCircle, XCircle, Clock, CalendarX, Pause, Play } from "@phosphor-icons/react";

const WEEKDAYS = [
  { idx: 0, short: "Mon", full: "Monday" },
  { idx: 1, short: "Tue", full: "Tuesday" },
  { idx: 2, short: "Wed", full: "Wednesday" },
  { idx: 3, short: "Thu", full: "Thursday" },
  { idx: 4, short: "Fri", full: "Friday" },
  { idx: 5, short: "Sat", full: "Saturday" },
  { idx: 6, short: "Sun", full: "Sunday" },
];

export default function ShopAvailability() {
  const [av, setAv] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/stores/my/availability");
      setAv(r.data);
    } catch {
      toast.error("Failed to load availability");
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (partial, successMsg) => {
    setSaving(true);
    try {
      const r = await api.patch("/stores/my/availability", partial);
      setAv(r.data);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  if (!av) return <div className="p-6">Loading…</div>;

  const openNow = av.is_open;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Store availability</h1>
        <p className="text-gray-600 text-sm">Control when your store accepts orders</p>
      </div>

      {/* Big status card */}
      <div className={`qd-card p-5 ${openNow ? "bg-green-50/60 border-green-200" : "bg-red-50/60 border-red-200"} border-2`}
        data-testid="availability-status-card">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${openNow ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            <Storefront weight="fill" size={24} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-black text-xl">
              {openNow ? "Store is open · Accepting orders" : "Store is closed"}
            </div>
            <div className="text-sm text-gray-600 mt-0.5">
              {openNow
                ? `Customers can place orders until ${av.close_time}`
                : (av.reason || "Currently not accepting orders")}
            </div>
          </div>
        </div>
      </div>

      {/* Online toggle */}
      <div className="qd-card p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#F8CB46]/30 flex items-center justify-center">
              {av.is_online ? <CheckCircle weight="fill" size={20} className="text-[#15803D]" /> : <XCircle weight="fill" size={20} className="text-red-600" />}
            </div>
            <div className="min-w-0">
              <div className="font-heading font-black text-base">Accept orders</div>
              <div className="text-xs text-gray-500">Master switch · {av.is_online ? "Online" : "Offline"}</div>
            </div>
          </div>
          <button onClick={() => patch({ is_online: !av.is_online }, av.is_online ? "Store taken offline" : "Store is now online")}
            disabled={saving}
            data-testid="availability-online-toggle"
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${av.is_online ? "bg-[#15803D]" : "bg-gray-300"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${av.is_online ? "translate-x-8" : "translate-x-1"}`} />
          </button>
        </div>
      </div>

      {/* Timings */}
      <div className="qd-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock weight="duotone" size={20} className="text-[#15803D]" />
          <h3 className="font-heading font-black text-base">Business hours</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1">Opens at</div>
            <input
              type="time"
              value={av.open_time}
              onChange={(e) => setAv({ ...av, open_time: e.target.value })}
              onBlur={(e) => patch({ open_time: e.target.value }, "Opening time updated")}
              className="qd-input font-mono"
              data-testid="availability-open-time"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1">Closes at</div>
            <input
              type="time"
              value={av.close_time}
              onChange={(e) => setAv({ ...av, close_time: e.target.value })}
              onBlur={(e) => patch({ close_time: e.target.value }, "Closing time updated")}
              className="qd-input font-mono"
              data-testid="availability-close-time"
            />
          </label>
        </div>
        <p className="text-xs text-gray-500">Times are in IST. Overnight hours supported (e.g. 22:00–02:00).</p>
      </div>

      {/* Weekly off */}
      <div className="qd-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CalendarX weight="duotone" size={20} className="text-[#15803D]" />
          <h3 className="font-heading font-black text-base">Weekly off</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => patch({ weekly_off: null }, "Weekly off cleared")}
            aria-pressed={av.weekly_off === null || av.weekly_off === undefined}
            className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition ${av.weekly_off === null || av.weekly_off === undefined ? "border-[#15803D] bg-[#15803D]/5 text-[#15803D]" : "border-gray-100 text-gray-700"}`}
            data-testid="weekly-off-none">
            None
          </button>
          {WEEKDAYS.map((d) => {
            const active = av.weekly_off === d.idx;
            return (
              <button key={d.idx}
                onClick={() => patch({ weekly_off: d.idx }, `Weekly off set to ${d.full}`)}
                aria-pressed={active}
                className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition ${active ? "border-[#15803D] bg-[#15803D]/5 text-[#15803D]" : "border-gray-100 text-gray-700 hover:border-gray-200"}`}
                data-testid={`weekly-off-${d.short.toLowerCase()}`}>
                {d.short}
              </button>
            );
          })}
        </div>
      </div>

      {/* Temp close */}
      <div className="qd-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          {av.temporarily_closed
            ? <Play weight="duotone" size={20} className="text-[#15803D]" />
            : <Pause weight="duotone" size={20} className="text-[#15803D]" />}
          <h3 className="font-heading font-black text-base">Temporary close</h3>
        </div>
        <p className="text-xs text-gray-500">Quickly pause new orders without changing your business hours.</p>

        {av.temporarily_closed ? (
          <div className="space-y-2">
            <div className="text-sm bg-red-50 text-red-700 p-3 rounded-xl">
              Currently paused {av.temp_close_reason ? `· ${av.temp_close_reason}` : ""}
            </div>
            <button onClick={() => patch({ temporarily_closed: false, temp_close_until: null, temp_close_reason: "" }, "Store reopened")}
              className="qd-btn-green text-sm" data-testid="availability-reopen">
              Reopen now
            </button>
          </div>
        ) : (
          <TempCloseForm onSubmit={(reason) => patch({ temporarily_closed: true, temp_close_reason: reason }, "Store temporarily closed")} />
        )}
      </div>
    </div>
  );
}

function TempCloseForm({ onSubmit }) {
  const [reason, setReason] = useState("");
  const presets = ["Rush hour", "Out for delivery", "Stock counting", "Maintenance"];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <button key={p} onClick={() => setReason(p)}
            className={`text-xs px-2.5 py-1.5 rounded-full border ${reason === p ? "bg-[#F8CB46]/30 border-[#F8CB46]" : "border-gray-200 hover:border-gray-300"}`}>
            {p}
          </button>
        ))}
      </div>
      <input className="qd-input" placeholder="Reason (optional, shown to customers)"
        value={reason} onChange={(e) => setReason(e.target.value)}
        data-testid="availability-temp-reason" />
      <button onClick={() => onSubmit(reason)}
        className="qd-btn-primary text-sm"
        data-testid="availability-pause-btn">
        Pause new orders
      </button>
    </div>
  );
}
